import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

const INVALIDATE_EVENT = "ja:invalidate-api";
type InvalidateDetail = { prefix: string };

export function invalidateApiData(prefix: string) {
  window.dispatchEvent(new CustomEvent(INVALIDATE_EVENT, { detail: { prefix } }));
}

export function useApiData<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(path));
  const reload = useCallback(async (options?: { background?: boolean }) => {
    if (!path) return;
    if (!options?.background) setLoading(true);
    setError("");
    try { setData(await api<T>(path)); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Falha ao carregar"); }
    finally { setLoading(false); }
  }, [path]);
  useEffect(() => {
    const frame = requestAnimationFrame(() => void reload());
    return () => cancelAnimationFrame(frame);
  }, [reload]);
  useEffect(() => {
    if (!path) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<InvalidateDetail>).detail;
      if (path.startsWith(detail.prefix)) void reload();
    };
    window.addEventListener(INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(INVALIDATE_EVENT, handler);
  }, [path, reload]);
  return { data, error, loading, reload };
}

export function useMediaQuery(query: string) {
  const getMatch = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getMatch);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

export const brl = (value: string) => {
  const match = value.match(/^(-?)([0-9]{1,12})\.([0-9]{2,4})$/);
  if (!match?.[2] || !match[3]) return value;
  const grouped = match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${match[1] ? "−" : ""}R$ ${grouped},${match[3]}`;
};

export const dateTime = (value: string) => new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo",
}).format(new Date(value));
